import React, { useState, useEffect } from "react";
import axios from "axios";
import CompanyDetails from "../new_interview_request/company_details";
import RoleDetails from "../new_interview_request/role_details";
import Message from "../interview_request2/message";
import ShortlistRecap from "../interview_request2/shortlist_recap";
import LoadingBlack from "../../images/loading-black.svg";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2, ChevronDown, ChevronUp, Check, X, Video, ArrowRight, FileText, Plus } from "lucide-react";

// SelectExistingSpotlightModal Component
function SelectExistingSpotlightModal({
  isOpen,
  onClose,
  spotlights = [],
  currentlySelectedId,
  onSelect,
}) {
  if (!isOpen) return null;

  const handleSelect = (spotlight) => {
    onSelect(spotlight);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-opacity duration-300"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl p-6 w-full max-w-4xl my-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Select a spotlight</h2>
          <button 
            onClick={onClose} 
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        {spotlights.length === 0 && (
          <div className="py-12 text-center">
            <Video size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600">No spotlights found yet for this category.</p>
          </div>
        )}
        
        {spotlights.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {spotlights.map((sp) => (
              <CompactSpotlightCard
                key={sp.id}
                spotlight={sp}
                selected={currentlySelectedId === sp.id}
                onSelect={handleSelect}
                onDelete={() => {}}
              />
            ))}
          </div>
        )}
        
        <div className="flex justify-end mt-6">
          <button 
            onClick={onClose} 
            className="px-5 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// AddSpotlightModal Component
function AddSpotlightModal({ isOpen, onClose, type, onAddSpotlight, spotlightToEdit }) {
  const isEditing = !!spotlightToEdit;
  const [name, setName] = useState("");
  const [loomUrl, setLoomUrl] = useState("");
  const [dropboxUrl, setDropboxUrl] = useState("");
  const [selectedSource, setSelectedSource] = useState("loom");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditing) {
      setName(spotlightToEdit.Name || "");
      const loom = spotlightToEdit["URL Loom cleaned"] || "";
      const dropbox = spotlightToEdit["URL Dropbox cleaned"] || "";
      setLoomUrl(loom);
      setDropboxUrl(dropbox);
      setSelectedSource(loom ? "loom" : dropbox ? "dropbox" : "loom");
    } else {
      setName("");
      setLoomUrl("");
      setDropboxUrl("");
      setSelectedSource("loom");
    }
  }, [isOpen, isEditing, spotlightToEdit]);

  if (!isOpen) return null;

  const handleSave = () => {
    setIsLoading(true);
    const updated = isEditing
      ? { ...spotlightToEdit }
      : {
          id: Date.now(),
          Type: type === "role" ? "Role information" : "Company information",
        };

    updated.Name = name.trim();
    if (selectedSource === "loom") {
      updated["URL Loom cleaned"] = loomUrl.trim();
      updated["URL Dropbox cleaned"] = "";
    } else {
      updated["URL Loom cleaned"] = "";
      updated["URL Dropbox cleaned"] = dropboxUrl.trim();
    }

    onAddSpotlight(updated);
    setIsLoading(false);
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto transition-opacity duration-300"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl p-6 w-full max-w-3xl my-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEditing ? "Edit spotlight video" : "Add a new spotlight"}
          </h2>
          <button 
            onClick={onClose} 
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div
              onClick={() => setSelectedSource("loom")}
              className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all duration-200 ${
                selectedSource === "loom" 
                  ? "border-[#625DF5] bg-[#625DF5]/5 shadow-sm" 
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">Loom</span>
                <a
                  href="https://www.loom.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#625DF5] hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Visit Loom
                </a>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Use Loom to record your video.
              </p>
            </div>
            <div
              onClick={() => setSelectedSource("dropbox")}
              className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all duration-200 ${
                selectedSource === "dropbox" 
                  ? "border-blue-500 bg-blue-50 shadow-sm" 
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">Dropbox</span>
                <a
                  href="https://www.dropbox.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Visit Dropbox
                </a>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Use Dropbox to upload your file.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recording Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jumpstart: Match Making"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#02B491] focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {selectedSource === "loom" ? "Loom Video URL" : "Dropbox File URL"}
              </label>
              <input
                type="url"
                value={selectedSource === "loom" ? loomUrl : dropboxUrl}
                onChange={(e) =>
                  selectedSource === "loom"
                    ? setLoomUrl(e.target.value)
                    : setDropboxUrl(e.target.value)
                }
                placeholder={
                  selectedSource === "loom"
                    ? "https://www.loom.com/your-video-link"
                    : "https://www.dropbox.com/s/..."
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#02B491] focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-8 space-x-3">
          <button 
            onClick={onClose} 
            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-2.5 bg-[#02B491] text-white rounded-lg hover:bg-[#029478] transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                Saving...
              </>
            ) : isEditing ? (
              "Save Changes"
            ) : (
              "Upload Spotlight"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// CompactSpotlightCard Component
function CompactSpotlightCard({ spotlight, selected, onSelect, onDelete }) {
  const loomUrl = spotlight["URL Loom cleaned"];
  const dropboxUrl = spotlight["URL Dropbox cleaned"];

  let previewElement = null;
  if (loomUrl) {
    const loomId = loomUrl.split("/").pop();
    const loomEmbed = `https://www.loom.com/embed/${loomId}`;
    previewElement = (
      <div className="aspect-video w-full bg-gray-50 rounded-t-lg overflow-hidden">
        <iframe
          title="Loom Preview"
          src={loomEmbed}
          frameBorder="0"
          allowFullScreen
          className="w-full h-full"
          sandbox="allow-same-origin allow-scripts allow-popups"
        />
      </div>
    );
  } else if (dropboxUrl) {
    const directVideoUrl = dropboxUrl.replace("?dl=0", "?raw=1");
    previewElement = (
      <div className="aspect-video w-full bg-gray-50 rounded-t-lg overflow-hidden">
        <video src={directVideoUrl} className="w-full h-full object-cover" />
      </div>
    );
  } else {
    previewElement = (
      <div className="aspect-video w-full bg-gray-50 rounded-t-lg flex items-center justify-center">
        <div className="flex flex-col items-center justify-center text-gray-400">
          <Video className="h-8 w-8 mb-2" />
          <p className="text-sm">No preview</p>
        </div>
      </div>
    );
  }

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete();
  };

  const handleSelect = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(spotlight);
  };

  return (
    <div
      onClick={handleSelect}
      className={`group bg-white rounded-lg transition-all duration-200 overflow-hidden cursor-pointer hover:shadow-md
        ${
          selected
            ? "ring-2 ring-[#02B491] shadow-md"
            : "border border-gray-200 hover:border-gray-300"
        }`}
    >
      <div className="cursor-pointer">{previewElement}</div>
      <div className="p-4 flex items-center justify-between border-t border-gray-100">
        <div className="flex-grow">
          <p className="text-sm font-medium text-gray-700 truncate">
            {spotlight.Name || "Untitled"}
          </p>
          {selected && (
            <p className="text-xs text-[#02B491] mt-1 flex items-center">
              <Check size={12} className="mr-1" /> Selected
            </p>
          )}
        </div>
        <button
          onClick={handleDelete}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-all duration-200"
          aria-label="Delete spotlight"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  );
}

// Main MatchRequestModal Component
function MatchRequestModal({ isOpen, onClose, role, candidate }) {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);
  const [roleData, setRoleData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [companyNeedUpdate, setCompanyNeedUpdate] = useState(false);
  const [roleNeedUpdate, setRoleNeedUpdate] = useState(false);
  const [activeStep, setActiveStep] = useState("Company Information");
  
  // UI state for collapsible sections
  const [companyExpanded, setCompanyExpanded] = useState(true);
  const [roleExpanded, setRoleExpanded] = useState(true);
  const [messageExpanded, setMessageExpanded] = useState(true);
  
  // Spotlight states
  const [spotlights, setSpotlights] = useState([]);
  const [companySpotlights, setCompanySpotlights] = useState([]);
  const [roleSpotlights, setRoleSpotlights] = useState([]);
  const [selectedCompanySpotlight, setSelectedCompanySpotlight] = useState(null);
  const [selectedRoleSpotlight, setSelectedRoleSpotlight] = useState(null);
  const [spotlightNeedUpdate, setSpotlightNeedUpdate] = useState(false);
  
  // Modal states
  const [showSelectOrUploadCompany, setShowSelectOrUploadCompany] = useState(false);
  const [showSelectOrUploadRole, setShowSelectOrUploadRole] = useState(false);
  const [showSelectSpotlightModal, setShowSelectSpotlightModal] = useState(false);
  const [selectSpotlightType, setSelectSpotlightType] = useState(null);
  const [showAddSpotlightModal, setShowAddSpotlightModal] = useState(false);
  const [addSpotlightType, setAddSpotlightType] = useState(null);
  const [spotlightToEdit, setSpotlightToEdit] = useState(null);

  // Helper functions to check if sections are complete
  const isCompanyComplete = () => {
    if (!companyData) return false;
    return (
      companyData.name && 
      companyData.description
    );
  };

  const isRoleComplete = () => {
    if (!roleData) return false;
    return (
      roleData.title && 
      roleData.compensation &&
      roleData.working_style &&
      roleData.equity &&
      (roleData.job_description_link || selectedRoleSpotlight)
    );
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    let steps = 0;
    let completed = 0;
    
    // Company section counts as 1 step
    steps++;
    if (isCompanyComplete()) completed++;
    
    // Role section counts as 1 step
    steps++;
    if (isRoleComplete()) completed++;
    
    // Message is the 3rd step but we don't track completion for it
    steps++;
    
    return Math.floor((completed / steps) * 100);
  };

  // Initial state setup when modal opens
  useEffect(() => {
    if (isOpen && role && candidate) {
      // Transform company data
      const transformedCompany = {
        id: role["StartupID"]?.[0] || role["id (from Startup)"]?.[0] || role["Startup"]?.[0],
        name: role["Name (from Startup)"]?.[0],
        description: role["Startup Description (from Startup)"]?.[0],
        location: role["Location (from Startup)"]?.[0],
        website: role["Website (from Startup)"]?.[0],
        funding: role["Funding Stage (from Startup)"]?.[0],
      };
      setCompanyData(transformedCompany);

      // Transform role data
      setRoleData({
        id: role.id,
        title: role["Role Title"],
        compensation: role["Compensation"],
        equity: role["Equity"],
        working_style: role["Working Style"],
        job_description_link: role["Job Description PDF"],
        key_experience: role["Specific Experience"],
        interview_process: role["Interview Process"],
        additional_info: role["Additional Information"],
        open_to_applications: role["Open to applications"] === "Open to apps",
        hiring_lead_name: role["Hiring Lead Name"],
        hiring_lead_email: role["Hiring Lead Email Address"],
        spotlight: role["Spotlight"],
        spotlight_context: role["Spotlight Context"],
      });

      fetchSpotlights();
      setIsLoading(false);
      setShowSuccess(false);
      
      // Set appropriate initial state after data is loaded
      setTimeout(() => {
        // Check if the role details are complete
        const hasJobDescription = role["Job Description PDF"] && role["Job Description PDF"].trim() !== '';
        const hasSpotlight = Array.isArray(role["Spotlight"]) && role["Spotlight"].length > 0;
        const hasRequiredFields = role["Role Title"] && 
                                  role["Compensation"] && 
                                  role["Working Style"] && 
                                  role["Equity"];
                                  
        const roleComplete = hasRequiredFields && (hasJobDescription || hasSpotlight);
                            
        if (!roleComplete) {
          // If role is incomplete, expand role section and set it as active step
          setRoleExpanded(true);
          setActiveStep("Role Details");
        }
      }, 100);
    } else {
      setCompanyData(null);
      setRoleData(null);
      setIsLoading(false);
      setShowSuccess(false);
    }
  }, [isOpen, role, candidate]);

  // Auto-collapse completed sections when data changes
  useEffect(() => {
    // Only auto-collapse the company section if it's complete and not being edited
    if (companyData && isCompanyComplete() && activeStep !== "Company Information") {
      setCompanyExpanded(false);
    } else if (companyData && !isCompanyComplete()) {
      // If not complete, ensure it's expanded and set as active step
      setCompanyExpanded(true);
      if (activeStep !== "Role Details" && activeStep !== "Message") {
        setActiveStep("Company Information");
      }
    }
    
    // Only auto-collapse the role section if it's complete and not being edited
    if (roleData && isRoleComplete() && activeStep !== "Role Details") {
      setRoleExpanded(false);
    } else if (roleData && !isRoleComplete()) {
      // If not complete, ensure it's expanded and set as active step
      setRoleExpanded(true);
      
      // Set active step to Role Details if company is complete or we're not already on company info
      if ((companyData && isCompanyComplete()) || activeStep !== "Company Information") {
        setActiveStep("Role Details");
      }
    }
  }, [companyData, roleData, selectedRoleSpotlight, activeStep]);

  // Effect to auto-advance to Message section when role is complete
  useEffect(() => {
    if (roleData && isRoleComplete() && !roleExpanded) {
      setActiveStep("Message");
    }
  }, [roleData, roleExpanded, selectedRoleSpotlight]);

  // Effect to scroll to the correct section based on activeStep
  useEffect(() => {
    if (isOpen && !isLoading && roleData) {
      setTimeout(() => {
        const sectionId = 
          activeStep === "Message" ? 'message-section' :
          activeStep === "Role Details" ? 'role-details-section' :
          'company-information-section';
          
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [isOpen, isLoading, roleData, activeStep]);

  // Keep message section expanded by default
  useEffect(() => {
    setMessageExpanded(true);
  }, []);

  // Fetch spotlights
  const fetchSpotlights = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const resp = await axios.get("/api/spotlights/fetchSpotlights", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      let sps = resp.data.spotlights || [];
      sps = sps.filter((s) => !s.Deleted);

      setSpotlights(sps);
      
      const comp = sps.filter((sp) =>
        ["Company information", "Company and Role"].includes(sp.Type)
      );
      const roleSps = sps.filter((sp) => sp.Type === "Role information");
      
      setCompanySpotlights(comp);
      setRoleSpotlights(roleSps);

      // Only set spotlights if they are assigned to the role
      if (role && role["Spotlight"] && Array.isArray(role["Spotlight"])) {
        const assignedIDs = role["Spotlight"];
        const assigned = sps.filter((s) => assignedIDs.includes(s.id));
        
        const assignedCompany = assigned.find((sp) =>
          ["Company information", "Company and Role"].includes(sp.Type)
        );
        const assignedRole = assigned.find((sp) => sp.Type === "Role information");
        
        setSelectedCompanySpotlight(assignedCompany || null);
        setSelectedRoleSpotlight(assignedRole || null);
      } else {
        // If no spotlights are assigned, set both to null
        setSelectedCompanySpotlight(null);
        setSelectedRoleSpotlight(null);
      }
    } catch (err) {
      console.error("Failed to load spotlights. Continuing with none.", err);
      setSpotlights([]);
      setCompanySpotlights([]);
      setRoleSpotlights([]);
      setSelectedCompanySpotlight(null);
      setSelectedRoleSpotlight(null);
    }
  };
  
  // Spotlight handlers
  const handleCompanySpotlightSelect = (spotlight) => {
    setSelectedCompanySpotlight(spotlight);
  };

  const handleRoleSpotlightSelect = (spotlight) => {
    setSelectedRoleSpotlight(spotlight);
  };

  const handleSpotlightDelete = (spotlightId) => {
    if (selectedCompanySpotlight && selectedCompanySpotlight.id === spotlightId) {
      setSelectedCompanySpotlight(null);
    }
    if (selectedRoleSpotlight && selectedRoleSpotlight.id === spotlightId) {
      setSelectedRoleSpotlight(null);
    }
  };

  const handleOpenCompanySelectOrUpload = () => {
    setShowSelectOrUploadCompany(true);
  };

  const handleOpenRoleSelectOrUpload = () => {
    setShowSelectOrUploadRole(true);
  };
  
  const closeAllSelectOrUploadModals = () => {
    setShowSelectOrUploadCompany(false);
    setShowSelectOrUploadRole(false);
    setShowSelectSpotlightModal(false);
    setAddSpotlightType(null);
    setSpotlightToEdit(null);
    setShowAddSpotlightModal(false);
  };

  const handleSelectSpotlight = (type) => {
    setSelectSpotlightType(type);
    setShowSelectOrUploadCompany(false);
    setShowSelectOrUploadRole(false);
    setShowSelectSpotlightModal(true);
  };

  const handleUploadSpotlight = (type) => {
    setShowSelectOrUploadCompany(false);
    setShowSelectOrUploadRole(false);
    setAddSpotlightType(type);
    setSpotlightToEdit(null);
    setShowAddSpotlightModal(true);
  };

  const onSelectExistingSpotlight = (sp) => {
    if (selectSpotlightType === "company") {
      setSelectedCompanySpotlight(sp);
    } else {
      setSelectedRoleSpotlight(sp);
    }
    setShowSelectSpotlightModal(false);
    setSelectSpotlightType(null);
  };
  
  const handleAddSpotlight = async (updated) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      
      // Handle existing spotlight
      if (updated.id && !updated.isNew) {
        await axios.patch("/api/spotlights/updateSpotlightData", 
          {
            fields: {
              "Name": updated.Name,
              "URL Loom": updated["URL Loom cleaned"],
              "URL Dropbox": updated["URL Dropbox cleaned"]
            },
            spotlightId: updated.id
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
      } else {
        // Create new spotlight
        const data = {
          fields: {
            "Name": updated.Name,
            "Type": updated.Type
          }
        };
        
        if (updated["URL Loom cleaned"]) {
          data.fields["URL Loom"] = updated["URL Loom cleaned"];
        }
        
        if (updated["URL Dropbox cleaned"]) {
          data.fields["URL Dropbox"] = updated["URL Dropbox cleaned"];
        }
        
        const response = await axios.post("/api/spotlights/createSpotlight", data, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        const newSpotlight = response.data.spotlight;
        
        // Select the newly created spotlight
        if (updated.Type === "Role information") {
          setSelectedRoleSpotlight(newSpotlight);
        } else {
          setSelectedCompanySpotlight(newSpotlight);
        }
      }
      
      // Refetch spotlights to update the list
      await fetchSpotlights();
      
    } catch (err) {
      console.error("Error saving spotlight:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle submission from Message component
  const handleSendRequest = async (messageData) => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      
      // Update Role with contact details if needed
      const roleUpdateData = {
        fields: {
          "Hiring Lead Name": messageData.interviewer_name,
          "Hiring Lead Email Address": messageData.interviewer_email,
          "Eye catching information": messageData.eye_catching_information,
          "Hiring Lead Calendly Link": messageData.calendly_link,
          "CCs": messageData.other_emails,
        },
        roleId: roleData.id,
      };
      
      // Update role with interviewer details
      await axios.patch("/api/roles/updateRoleData", roleUpdateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      // Create interview record
      const interviewData = {
        fields: {
          Role: [roleData.id],
          Candidate: [candidate.id],
          "Eye Catching Information": messageData.eye_catching_information,
          Stage: "Match requested",
          "Match type": "Match Request",
          ...(candidate.most_recent_cohort && candidate.most_recent_cohort.length > 0 && {
            "Matching cohort": candidate.most_recent_cohort[0],
          }),
        },
      };
      
      const response = await axios.post(
        "/api/interviews/createInterview",
        interviewData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      console.log(`Interview created for candidate ${candidate.id}:`, response.data);
      
      setIsLoading(false);
      setShowSuccess(true);
    } catch (error) {
      console.error("Error creating interview request:", error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const progressPercentage = calculateProgress();

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto transition-opacity duration-300"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl h-[90vh] overflow-hidden relative my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-20"
          aria-label="Close modal"
        >
          <X size={18} className="text-gray-600" />
        </button>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm transition-opacity duration-300">
            <div className="flex flex-col items-center">
              <img src={LoadingBlack} className="h-12 w-12 animate-spin" alt="Loading"/>
              <p className="mt-4 text-gray-600 font-medium">Processing request...</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {showSuccess ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center p-8 max-w-md">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                Interview Request Sent!
              </h2>
              <p className="text-gray-600 mb-8">
                We've sent your request to <span className="font-semibold">{candidate.name}</span>. 
                You should expect to hear back within the next couple of days!
              </p>
              <button
                className="px-8 py-3 bg-[#02B491] hover:bg-[#029478] text-white font-medium rounded-lg transition-colors shadow-sm"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Main Modal Content */
          <div className="flex flex-col h-full">
            {/* Header and Progress */}
            <div className="px-6 py-4 border-b sticky top-0 bg-white z-10 flex flex-col">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Request Interview with {candidate.name}
                </h2>
                <div className="text-sm font-medium text-gray-500">
                  {progressPercentage < 100 ? `${progressPercentage}% Complete` : 'Ready to submit'}
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="w-full h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden">
                <div 
                  className="h-full bg-[#02B491] rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
            
            {/* Content area with scrolling */}
            <div className="flex flex-col md:flex-row h-full overflow-hidden">
              {/* Left Side: Details */}
              <div className="flex-grow w-full md:w-2/3 overflow-y-auto p-6 space-y-6">
                {/* Company Information - Collapsible Section */}
                {companyData && (
                  <div 
                    id="company-information-section" 
                    className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                      activeStep === "Company Information" 
                        ? "border-[#02B491] shadow-md" 
                        : "border-gray-200"
                    } ${isCompanyComplete() ? "bg-white" : "bg-white"}`}
                  >
                    <div 
                      className={`px-5 py-4 flex justify-between items-center cursor-pointer ${
                        isCompanyComplete() 
                          ? "bg-gradient-to-r from-green-50 to-white border-l-4 border-l-green-500" 
                          : activeStep === "Company Information" 
                            ? "bg-[#02B491]/5" 
                            : "bg-gray-50"
                      }`}
                      onClick={() => setCompanyExpanded(!companyExpanded)}
                    >
                      <div className="flex items-center">
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center mr-3 text-sm font-medium ${
                          isCompanyComplete() 
                            ? "bg-green-500 text-white" 
                            : "bg-gray-200 text-gray-600"
                        }`}>
                          {isCompanyComplete() ? <Check size={14} /> : "1"}
                        </div>
                        <h3 className="text-lg font-medium text-gray-800">Company Information</h3>
                        {isCompanyComplete() && (
                          <span className="ml-3 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center">
                            <Check size={12} className="mr-1" /> Complete
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        {!companyExpanded && (
                          <div className="mr-3 text-sm text-gray-600 hidden md:block">
                            {companyData.name}
                          </div>
                        )}
                        <button className="text-gray-500">
                          {companyExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Collapsed summary or expanded details */}
                    {companyExpanded ? (
                      <div className="p-5">
                        <CompanyDetails
                          activeStep={activeStep}
                          setActiveStep={setActiveStep}
                          company={companyData}
                          setCompany={setCompanyData}
                          setCompanyNeedUpdate={setCompanyNeedUpdate}
                          loading={isLoading}
                        />
                      </div>
                    ) : (
                      <div className="p-4 bg-white">
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center">
                            <span className="font-medium w-24 text-gray-600">Company:</span>
                            <span className="font-medium">{companyData.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-24 text-gray-600">Location:</span>
                            <span>{companyData.location || 'N/A'}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="font-medium w-24 text-gray-600 pt-0.5">Description:</span>
                            <span className="line-clamp-1 flex-1">{companyData.description || 'N/A'}</span>
                          </div>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setCompanyExpanded(true);
                              setTimeout(() => {
                                setActiveStep("Company Information");
                              }, 50);
                            }}
                            className="self-end text-sm text-[#02B491] hover:text-[#029478] flex items-center"
                          >
                            <Edit2 size={14} className="mr-1" /> Edit details
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Role Details - Collapsible Section */}
                {roleData && (
                  <div 
                    id="role-details-section" 
                    className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                      activeStep === "Role Details" 
                        ? "border-[#02B491] shadow-md" 
                        : "border-gray-200"
                    } ${isRoleComplete() ? "bg-white" : "bg-white"}`}
                  >
                    <div 
                      className={`px-5 py-4 flex justify-between items-center cursor-pointer ${
                        isRoleComplete() 
                          ? "bg-gradient-to-r from-green-50 to-white border-l-4 border-l-green-500" 
                          : activeStep === "Role Details" 
                            ? "bg-[#02B491]/5" 
                            : "bg-gray-50"
                      }`}
                      onClick={() => setRoleExpanded(!roleExpanded)}
                    >
                      <div className="flex items-center">
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center mr-3 text-sm font-medium ${
                          isRoleComplete() 
                            ? "bg-green-500 text-white" 
                            : "bg-gray-200 text-gray-600"
                        }`}>
                          {isRoleComplete() ? <Check size={14} /> : "2"}
                        </div>
                        <h3 className="text-lg font-medium text-gray-800">Role Details</h3>
                        {isRoleComplete() && (
                          <span className="ml-3 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center">
                            <Check size={12} className="mr-1" /> Complete
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        {!roleExpanded && (
                          <div className="mr-3 text-sm text-gray-600 hidden md:block">
                            {roleData.title}, {roleData.compensation}
                          </div>
                        )}
                        <button className="text-gray-500">
                          {roleExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Collapsed summary or expanded details */}
                    {roleExpanded ? (
                      <div className="p-5">
                        <RoleDetails
                          activeStep={activeStep}
                          setActiveStep={setActiveStep}
                          role={roleData}
                          setRole={setRoleData}
                          setRoleNeedUpdate={setRoleNeedUpdate}
                          loading={isLoading}
                          // Add spotlight props
                          selectedCompanySpotlight={selectedCompanySpotlight}
                          selectedRoleSpotlight={selectedRoleSpotlight}
                          companySpotlights={companySpotlights}
                          roleSpotlights={roleSpotlights}
                          onCompanySpotlightSelect={handleCompanySpotlightSelect}
                          onRoleSpotlightSelect={handleRoleSpotlightSelect}
                          onSpotlightDelete={handleSpotlightDelete}
                          onOpenCompanySelectOrUpload={handleOpenCompanySelectOrUpload}
                          onOpenRoleSelectOrUpload={handleOpenRoleSelectOrUpload}
                        />
                      </div>
                    ) : (
                      <div className="p-4 bg-white">
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center">
                            <span className="font-medium w-24 text-gray-600">Role Title:</span>
                            <span className="font-medium">{roleData.title || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-24 text-gray-600">Compensation:</span>
                            <span>{roleData.compensation || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-24 text-gray-600">Working Style:</span>
                            <span>{roleData.working_style || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-24 text-gray-600">Job Desc:</span>
                            <span>
                              {roleData.job_description_link ? (
                                <a 
                                  href={roleData.job_description_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline flex items-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FileText size={14} className="mr-1" /> View PDF
                                </a>
                              ) : selectedRoleSpotlight ? (
                                <span className="text-green-600 flex items-center">
                                  <Video size={14} className="mr-1" /> Spotlight Available
                                </span>
                              ) : (
                                'Not provided'
                              )}
                            </span>
                          </div>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setRoleExpanded(true);
                              setTimeout(() => {
                                setActiveStep("Role Details");
                              }, 50);
                            }}
                            className="self-end text-sm text-[#02B491] hover:text-[#029478] flex items-center"
                          >
                            <Edit2 size={14} className="mr-1" /> Edit details
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Message Section */}
                {roleData && (
                  <div 
                    id="message-section" 
                    className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                      activeStep === "Message" 
                        ? "border-[#02B491] shadow-xl" 
                        : isCompanyComplete() && isRoleComplete() 
                          ? "border-blue-300 shadow-md" 
                          : "border-gray-200"
                    } ${activeStep === "Message" ? "bg-blue-50/30" : "bg-white"}`}
                  >
                    <div className={`px-5 py-4 flex justify-between items-center ${
                      activeStep === "Message" 
                        ? "bg-blue-100 text-blue-800"
                        : isCompanyComplete() && isRoleComplete()
                          ? "bg-blue-50 text-blue-800"
                          : "bg-gray-50 text-gray-800"
                    }`}>
                      <div className="flex items-center">
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center mr-3 text-sm font-medium ${
                          activeStep === "Message" 
                            ? "bg-blue-600 text-white" 
                            : isCompanyComplete() && isRoleComplete()
                              ? "bg-blue-500 text-white"
                              : "bg-gray-200 text-gray-600"
                        }`}>
                          3
                        </div>
                        <h3 className="text-lg font-medium">Message Details</h3>
                        {isCompanyComplete() && isRoleComplete() && (
                          <span className="ml-3 bg-blue-600 text-white text-xs px-2 py-1 rounded-full flex items-center">
                            Ready to complete
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-5">
                      {(!isCompanyComplete() || !isRoleComplete()) ? (
                        <div className="py-6 text-center">
                          <p className="text-gray-500">
                            Complete the company and role details before proceeding to this step
                          </p>
                          <button 
                            onClick={() => {
                              if (!isCompanyComplete()) {
                                setActiveStep("Company Information");
                                setCompanyExpanded(true);
                                document.getElementById('company-information-section')?.scrollIntoView({ 
                                  behavior: 'smooth', 
                                  block: 'start' 
                                });
                              } else {
                                setActiveStep("Role Details");
                                setRoleExpanded(true);
                                document.getElementById('role-details-section')?.scrollIntoView({ 
                                  behavior: 'smooth', 
                                  block: 'start' 
                                });
                              }
                            }}
                            className="mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center mx-auto"
                          >
                            <ArrowRight size={16} className="mr-2" />
                            Go to {!isCompanyComplete() ? "Company Information" : "Role Details"}
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="text-blue-700 font-medium mb-6">
                            Please fill in your message details below to finalize your interview request.
                          </p>
                          <Message
                            activeStep="Message" 
                            role={roleData}
                            sendRequest={handleSendRequest}
                            candidates={[candidate]}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side: Candidate Recap */}
              <div className="w-full md:w-1/3 md:border-l border-gray-200 md:overflow-y-auto">
                <div className="sticky top-0 p-6">
                  <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h3 className="font-medium text-gray-800">Candidate Summary</h3>
                    </div>
                    <div className="p-4">
                      <ShortlistRecap candidates={[candidate]} loading={!candidate} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company Spotlight Select/Upload Modal */}
        {showSelectOrUploadCompany && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm p-4 transition-opacity duration-300"
            onClick={closeAllSelectOrUploadModals}
          >
            <div
              className="bg-white p-6 rounded-xl max-w-sm w-full relative shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeAllSelectOrUploadModals}
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close modal"
              >
                <X size={18} className="text-gray-500" />
              </button>
              
              <div className="text-center mb-6">
                <Video size={40} className="mx-auto mb-3 text-[#02B491]" />
                <h3 className="text-lg font-semibold mb-1">Company Spotlight</h3>
                <p className="text-gray-600 text-sm">Add a video showcase for your company</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-[#02B491] hover:bg-[#02B491]/5 transition-all"
                  onClick={() => handleSelectSpotlight("company")}
                >
                  <FileText size={24} className="mb-2 text-gray-600" />
                  <span className="text-sm font-medium">Select existing</span>
                </button>
                <button
                  className="flex flex-col items-center p-4 border border-[#02B491] bg-[#02B491]/5 rounded-lg hover:bg-[#02B491] hover:text-white group transition-all"
                  onClick={() => handleUploadSpotlight("company")}
                >
                  <Plus size={24} className="mb-2 text-[#02B491] group-hover:text-white transition-colors" />
                  <span className="text-sm font-medium">Upload new</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Role Spotlight Select/Upload Modal */}
        {showSelectOrUploadRole && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm p-4 transition-opacity duration-300"
            onClick={closeAllSelectOrUploadModals}
          >
            <div
              className="bg-white p-6 rounded-xl max-w-sm w-full relative shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeAllSelectOrUploadModals}
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close modal"
              >
                <X size={18} className="text-gray-500" />
              </button>
              
              <div className="text-center mb-6">
                <Video size={40} className="mx-auto mb-3 text-[#02B491]" />
                <h3 className="text-lg font-semibold mb-1">Role Spotlight</h3>
                <p className="text-gray-600 text-sm">Add a video showcase for your role</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-[#02B491] hover:bg-[#02B491]/5 transition-all"
                  onClick={() => handleSelectSpotlight("role")}
                >
                  <FileText size={24} className="mb-2 text-gray-600" />
                  <span className="text-sm font-medium">Select existing</span>
                </button>
                <button
                  className="flex flex-col items-center p-4 border border-[#02B491] bg-[#02B491]/5 rounded-lg hover:bg-[#02B491] hover:text-white group transition-all"
                  onClick={() => handleUploadSpotlight("role")}
                >
                  <Plus size={24} className="mb-2 text-[#02B491] group-hover:text-white transition-colors" />
                  <span className="text-sm font-medium">Upload new</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Select Existing Spotlight Modal */}
        <SelectExistingSpotlightModal
          isOpen={showSelectSpotlightModal}
          onClose={closeAllSelectOrUploadModals}
          spotlights={
            selectSpotlightType === "company"
              ? companySpotlights
              : selectSpotlightType === "role"
              ? roleSpotlights
              : []
          }
          currentlySelectedId={
            selectSpotlightType === "company"
              ? selectedCompanySpotlight?.id
              : selectedRoleSpotlight?.id
          }
          onSelect={onSelectExistingSpotlight}
        />

        {/* Add/Edit Spotlight Modal */}
        <AddSpotlightModal
          isOpen={showAddSpotlightModal}
          onClose={() => {
            setShowAddSpotlightModal(false);
            setSpotlightToEdit(null);
            setAddSpotlightType(null);
          }}
          type={addSpotlightType}
          onAddSpotlight={handleAddSpotlight}
          spotlightToEdit={spotlightToEdit}
        />
      </div>
    </div>
  );
}

export default MatchRequestModal;